/**
 * AdminReAuthModal
 *
 * Shown to admin users when their session is within 5 minutes of expiry.
 * Allows inline re-authentication via Manus OAuth without navigating away
 * (the OAuth flow redirects back to the current page via the state parameter).
 *
 * Non-admin users continue to use the existing sonner toast (see useSessionExpiry.ts).
 */
import { getLoginUrl } from "@/const";
import { useEffect, useRef, useState } from "react";

interface AdminReAuthModalProps {
  /** Whether the modal is visible */
  open: boolean;
  /** Milliseconds until the session expires (used to drive the countdown) */
  msUntilExpiry: number;
  /** Called when the user clicks "Dismiss" - triggers logout/redirect to login */
  onDismiss: () => void;
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return "0:00";
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export default function AdminReAuthModal({
  open,
  msUntilExpiry,
  onDismiss,
}: AdminReAuthModalProps) {
  // Live countdown driven by a 1-second interval inside the modal
  const [displayMs, setDisplayMs] = useState(msUntilExpiry);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Sync displayMs when the parent updates msUntilExpiry (e.g. on each poll)
  useEffect(() => {
    setDisplayMs(msUntilExpiry);
  }, [msUntilExpiry]);

  // Tick the countdown every second while the modal is open
  useEffect(() => {
    if (!open) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setDisplayMs(prev => {
        const next = prev - 1000;
        return next < 0 ? 0 : next;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [open]);

  if (!open) return null;

  const isUrgent = displayMs <= 60_000; // last 60 seconds → red accent

  const handleReAuth = () => {
    // Redirect to OAuth; the state parameter encodes the current origin so
    // the callback will redirect back to this page after authentication.
    window.location.href = getLoginUrl();
  };

  return (
    /* Full-screen backdrop */
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reauth-title"
      aria-describedby="reauth-desc"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.72)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        padding: "1rem",
      }}
    >
      {/* Modal card */}
      <div
        style={{
          backgroundColor: "#0F1419",
          border: "1px solid rgba(255, 140, 0, 0.35)",
          borderRadius: "12px",
          padding: "2rem",
          maxWidth: "420px",
          width: "100%",
          boxShadow: "0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,140,0,0.1)",
          animation: "reauth-scale-in 220ms cubic-bezier(0.23, 1, 0.32, 1) both",
        }}
      >
        {/* Icon */}
        <div
          style={{
            width: "48px",
            height: "48px",
            borderRadius: "50%",
            backgroundColor: "rgba(255, 140, 0, 0.12)",
            border: "1px solid rgba(255, 140, 0, 0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "1.25rem",
          }}
          aria-hidden="true"
        >
          {/* Lock SVG */}
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#FF8C00"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        {/* Title */}
        <h2
          id="reauth-title"
          style={{
            color: "#F5F5F5",
            fontSize: "1.125rem",
            fontWeight: 600,
            marginBottom: "0.5rem",
            lineHeight: 1.3,
          }}
        >
          Admin session expiring
        </h2>

        {/* Description */}
        <p
          id="reauth-desc"
          style={{
            color: "#9CA3AF",
            fontSize: "0.875rem",
            lineHeight: 1.6,
            marginBottom: "1.5rem",
          }}
        >
          Your admin session will expire in{" "}
          <span
            style={{
              fontVariantNumeric: "tabular-nums",
              fontWeight: 700,
              color: isUrgent ? "#EF4444" : "#FF8C00",
              transition: "color 0.3s ease",
            }}
          >
            {formatCountdown(displayMs)}
          </span>
          . Re-authenticate to continue without losing your work.
        </p>

        {/* Actions */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem",
          }}
        >
          {/* Primary: Re-authenticate */}
          <button
            onClick={handleReAuth}
            style={{
              backgroundColor: "#FF8C00",
              color: "#0F1419",
              border: "none",
              borderRadius: "8px",
              padding: "0.75rem 1.25rem",
              fontSize: "0.9375rem",
              fontWeight: 700,
              cursor: "pointer",
              width: "100%",
              transition: "background-color 150ms ease, transform 100ms ease",
              letterSpacing: "0.01em",
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#E67E00";
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#FF8C00";
            }}
            onMouseDown={e => {
              (e.currentTarget as HTMLButtonElement).style.transform = "scale(0.97)";
            }}
            onMouseUp={e => {
              (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)";
            }}
          >
            Re-authenticate
          </button>

          {/* Secondary: Dismiss (sign out) */}
          <button
            onClick={onDismiss}
            style={{
              backgroundColor: "transparent",
              color: "#6B7280",
              border: "1px solid rgba(107, 114, 128, 0.3)",
              borderRadius: "8px",
              padding: "0.75rem 1.25rem",
              fontSize: "0.875rem",
              fontWeight: 500,
              cursor: "pointer",
              width: "100%",
              transition: "color 150ms ease, border-color 150ms ease, transform 100ms ease",
            }}
            onMouseEnter={e => {
              const btn = e.currentTarget as HTMLButtonElement;
              btn.style.color = "#9CA3AF";
              btn.style.borderColor = "rgba(156, 163, 175, 0.5)";
            }}
            onMouseLeave={e => {
              const btn = e.currentTarget as HTMLButtonElement;
              btn.style.color = "#6B7280";
              btn.style.borderColor = "rgba(107, 114, 128, 0.3)";
            }}
            onMouseDown={e => {
              (e.currentTarget as HTMLButtonElement).style.transform = "scale(0.97)";
            }}
            onMouseUp={e => {
              (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)";
            }}
          >
            Dismiss &amp; sign out
          </button>
        </div>

        {/* Fine print */}
        <p
          style={{
            color: "#4B5563",
            fontSize: "0.75rem",
            marginTop: "1.25rem",
            textAlign: "center",
            lineHeight: 1.5,
          }}
        >
          Re-authentication opens the Manus sign-in page and returns you here automatically.
        </p>
      </div>

      {/* Keyframe animation injected inline so it works without a CSS file */}
      <style>{`
        @keyframes reauth-scale-in {
          from { opacity: 0; transform: scale(0.95) translateY(8px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);   }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes reauth-scale-in {
            from { opacity: 0; }
            to   { opacity: 1; }
          }
        }
      `}</style>
    </div>
  );
}
